import { h, Host, } from "@stencil/core";
import { Link, router } from "@eventstore-ui/router";
import { theme } from "@eventstore-ui/theme";
import { debounce, rateLimit } from "@eventstore-ui/utils";
import { TableHeader } from "../TableHeader";
import { cellClasses } from "../utils/cellClasses";
import { autoExtract } from "../utils/autoExtract";
const MAX_TABLE_HEIGHT = 15000000;
/** Create a virtualized table from data. */
export class Table {
  constructor() {
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.renderRowGroup = (key, index) => (h("div", { role: 'rowgroup', key: key, class: {
        odd: this.invertBanding ? index % 2 !== 0 : index % 2 === 0,
        highlight: this.jumpHighlight === key,
      } }, this.renderRow(key, index)));
    this.renderRow = (key, index) => {
      const data = this.getCellData(key, index);
      if (this.linkRowTo) {
        return (h(Link, { url: this.linkRowTo(data), role: 'row', key: key, "aria-rowindex": index, class: this.rowClass(data, key, index), onClick: this.emitRowClick({ data, row: key, key, index }), tabindex: '-1' }, this.renderCells(data, key, index)));
      }
      return (h("div", { role: 'row', "aria-rowindex": index, key: key, class: this.rowClass(data, key, index), onClick: this.emitRowClick({ data, row: key, key, index }), tabindex: '-1' }, this.renderCells(data, key, index)));
    };
    this.renderCells = (data, key, index) => this.getColumnGroups().map(([_, cells], groupIndex, groups) => cells.map(([name, cell], cellIndex, cells) => {
      const focusCell = groupIndex === 0 &&
        cellIndex === 0 &&
        (!!this.rowTakesFocus || !!this.linkRowTo);
      return (h("div", { role: 'cell', tabindex: focusCell ? '0' : undefined, onKeyDown: focusCell
          ? this.focusCellKeyPress({
            index,
            key,
            row: key,
            data,
          })
          : undefined, style: {
          gridRowStart: `${this.calculateGridRow(index)}`,
        }, class: cellClasses(cell, data, focusCell, {
          groupIndex,
          groupCount: groups.length,
          cellIndex,
          cellCount: cells.length,
        }) }, cell.cell
        ? cell.cell(h, {
          ...(this.extraCellProps?.(key, data) ?? {}),
          key,
          data,
          parent: this.identifier,
        })
        : autoExtract(data, name)));
    }));
    this.focusCellKeyPress = (data) => (e) => {
      if (e.code !== 'Space' && e.code !== 'Enter')
        return;
      this.clickRow.emit(data);
      if (this.linkRowTo) {
        const link = this.linkRowTo(data);
        router.history?.push(link);
      }
    };
    this.emitRowClick = (data) => () => {
      this.clickRow.emit(data);
    };
    this.getCell = (col) => {
      return this.cells[col] ?? { title: '' };
    };
    this.getColumns = () => {
      if (this.columns)
        return this.columns;
      return Object.keys(this.cells);
    };
    this.getColumnGroups = () => {
      return this.getColumns().reduce((acc, col) => {
        const cell = this.getCell(col);
        if (cell.variant === 'exclude')
          return acc;
        if (acc.at(-1) != null && acc.at(-1)[0] === cell.group) {
          acc.at(-1)[1].push([col, cell]);
        }
        else {
          acc.push([cell.group, [[col, cell]]]);
        }
        return acc;
      }, []);
    };
    this.gridTemplateColumns = () => this.getColumns().reduce((acc, col) => {
      const cell = this.getCell(col);
      if (cell.variant === 'exclude')
        return acc;
      return `${acc} ${cell.width ?? 'auto'}`;
    }, '');
    this.gridTemplateRows = () => {
      return `repeat(${this.headerRows}, ${this.headerHeight}px) [before] ${this.beforeHeight}px repeat(${this.rowCount - this.reflowRow}, ${this.rowHeight}px) [after] ${this.afterHeight}px `;
    };
    this.calcHeaderRows = () => {
      if (this.headless)
        return 0;
      if (this.getColumnGroups().some(([g]) => g != null))
        return 2;
      return 1;
    };
    this.getScrollParent = () => {
      const canScroll = (node) => {
        const style = getComputedStyle(node, null);
        return (style.getPropertyValue('overflow') === 'scroll' ||
          style.getPropertyValue('overflow') === 'auto' ||
          style.getPropertyValue('overflow-y') === 'scroll' ||
          style.getPropertyValue('overflow-y') === 'auto' ||
          style.getPropertyValue('overflow-x') === 'scroll' ||
          style.getPropertyValue('overflow-x') === 'auto');
      };
      const scrollParent = (node) => {
        const parent = node.parentElement ?? node.parentNode?.parentElement;
        if (!parent) {
          return document.scrollingElement || document.documentElement;
        }
        if (canScroll(parent))
          return parent;
        return scrollParent(parent);
      };
      return scrollParent(this.host);
    };
    this.rowPosition = (row) => row * this.rowHeight;
    this.fullHeight = () => this.headerHeight +
      this.beforeHeight +
      this.rowPosition(this.rowCount) +
      this.afterHeight;
    this.applyScrollLock = () => {
      if (!this.prependCount)
        return;
      this.scrollParent.scrollTop += this.prependCount * this.rowHeight;
      delete this.prependCount;
      this.calculateWarp();
      this.calculateWindowing();
    };
    this.handleResize = () => {
      this.applyScrollLock();
    };
    this.previousScrollTop = 0;
    this.handleScroll = rateLimit(() => {
      this.calculateWarp();
      this.calculateWindowing();
    }, 10);
    this.internalScrollTop = 0;
    this.warpedScrollTop = 0;
    this.justWarped = false;
    this.calculateWarp = () => {
      const scrollTop = this.scrollParent.scrollTop;
      const distance = scrollTop - this.previousScrollTop;
      const warpFactor = this.warpFactor(this.internalScrollTop + distance);
      this.previousScrollTop = scrollTop;
      if (this.justWarped) {
        this.justWarped = false;
      }
      else if (warpFactor === 1) {
        this.internalScrollTop += distance;
        this.warpedScrollTop = this.internalScrollTop;
      }
      else if (Math.abs(distance) > 10000) {
        const jumpWarp = this.warpFactor(scrollTop);
        this.internalScrollTop = Math.round(scrollTop / jumpWarp);
        this.warpedScrollTop = scrollTop;
        this.warp = this.internalScrollTop - this.warpedScrollTop;
      }
      else {
        this.internalScrollTop += distance;
        this.warpedScrollTop = Math.round(this.internalScrollTop * warpFactor);
        this.applyWarp();
      }
    };
    this.warpFactor = (scrollTop) => {
      const expectedTableHeight = this.fullHeight();
      if (expectedTableHeight <= MAX_TABLE_HEIGHT)
        return 1;
      const actualHeight = this.scrollParent.scrollHeight - this.scrollParent.clientHeight;
      const expectedHeight = expectedTableHeight + (actualHeight - MAX_TABLE_HEIGHT);
      const distanceToEdge = Math.min(scrollTop, expectedHeight - scrollTop);
      // We don't want to warp at the edges
      if (distanceToEdge < actualHeight / 8) {
        return 1;
      }
      return actualHeight / expectedHeight;
    };
    this.applyWarp = debounce(() => {
      this.justWarped = true;
      if (this.scrollParent.scrollTop === this.warpedScrollTop) {
        return;
      }
      this.scrollParent.scrollTop = this.warpedScrollTop;
      this.warp = this.internalScrollTop - this.warpedScrollTop;
    }, 1000);
    this.calculateWindowing = () => {
      if (this.rowCount <= this.windowSize) {
        this.trailingEdge = 0;
        this.reflowRow = 0;
        return;
      }
      const top = Math.floor(this.internalScrollTop / this.rowHeight);
      const offsetTop = Math.max(0, top - this.windowSize / 4);
      const blockTop = offsetTop - (offsetTop % this.blockSize);
      const lastRow = this.rowCount;
      const lastBlock = lastRow - (lastRow % this.blockSize);
      const trailingEdge = Math.min(blockTop, lastBlock);
      this.trailingEdge = trailingEdge;
      this.reflowRow = trailingEdge - (trailingEdge % this.reflowSize);
    };
    this.calculateGridRow = (index) => {
      return index + 2 + this.headerRows - this.reflowRow;
    };
    this.hasRenderedWithCurrentRowCount = () => {
      const matches = (r) => {
        requestAnimationFrame(() => {
          if (this.rowCount === this.lastRenderRowCount) {
            return r();
          }
          return matches(r);
        });
      };
      return new Promise(matches);
    };
    this.internalJumpToRow = (index, { smooth, highlight }) => {
      const fullHeight = this.fullHeight();
      const trueHeight = this.host.offsetHeight;
      const scrollHeight = this.scrollParent.scrollHeight;
      const expectedTop = this.rowPosition(index);
      const realTop = (() => {
        // no warp needed
        if (fullHeight === trueHeight) {
          return expectedTop;
        }
        // at top edge, no warp
        const topEdge = Math.floor(MAX_TABLE_HEIGHT / 8 / this.rowHeight);
        if (index <= topEdge) {
          return Math.max(0, expectedTop);
        }
        // at bottom edge, no warp from bottom
        const bottomEdge = this.rowCount - topEdge;
        if (index >= bottomEdge) {
          return (scrollHeight -
            (this.scrollParent.clientHeight - this.host.offsetTop) -
            Math.max(0, fullHeight - expectedTop));
        }
        // somewhere in the middle, standard warp
        return (expectedTop *
          ((scrollHeight - this.scrollParent.clientHeight) / fullHeight));
      })();
      const distance = Math.abs(this.scrollParent.scrollTop - realTop);
      if (fullHeight === trueHeight &&
        smooth === 'auto' &&
        distance < this.windowSize * this.rowHeight) {
        this.scrollParent.scrollTo({
          top: realTop,
          behavior: 'smooth',
        });
      }
      else {
        this.scrollParent.scrollTop = realTop;
        this.internalScrollTop = expectedTop;
        this.warpedScrollTop = realTop;
        this.warp = this.internalScrollTop - this.warpedScrollTop;
        this.justWarped = true;
      }
      this.calculateWindowing();
      if (highlight) {
        this.jumpHighlight = this.getKeyFromIndex(index);
      }
    };
    this.identifier = 'table-virtualized';
    this.headless = false;
    this.stickyHeader = true;
    this.getCellData = undefined;
    this.getKeyFromIndex = (i) => `${i}`;
    this.cells = undefined;
    this.columns = undefined;
    this.rowCount = undefined;
    this.renderBefore = undefined;
    this.renderAfter = undefined;
    this.windowSize = 100;
    this.blockSize = 10;
    this.reflowSize = 1000;
    this.linkRowTo = undefined;
    this.rowHeight = 50;
    this.headerHeight = 52;
    this.beforeHeight = 0;
    this.afterHeight = 0;
    this.rowTakesFocus = undefined;
    this.rowClass = () => undefined;
    this.sort = undefined;
    this.scrollLock = undefined;
    this.extraCellProps = undefined;
    this.trailingEdge = 0;
    this.reflowRow = 0;
    this.warp = 0;
    this.jumpHighlight = null;
    this.invertBanding = false;
  }
  componentWillLoad() {
    this.scrollParent = this.getScrollParent();
    window.addEventListener('scroll', this.handleScroll, {
      passive: true,
    });
    this.resizeObserver.observe(this.host);
    this.headerRows = this.calcHeaderRows();
    this.initial = this.getKeyFromIndex(0);
    this.trailingEdgeChange(this.trailingEdge);
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver.disconnect();
    this.handleScroll.clear();
    this.applyWarp.clear();
    clearTimeout(this.jumpHighlightTimeout);
  }
  jumpHighlightChange(key) {
    clearTimeout(this.jumpHighlightTimeout);
    this.jumpHighlightTimeout = setTimeout(() => {
      if (this.jumpHighlight === key) {
        this.jumpHighlight = null;
      }
    }, 2000);
  }
  sortChange(from, to) {
    if (from?.[0] === to?.[0] && from?.[1] === to?.[1])
      return;
    this.trailingEdgeChange(this.trailingEdge);
  }
  async rowCountChange(next, previous) {
    const initial = this.getKeyFromIndex(0);
    if (initial !== this.initial) {
      this.initial = initial;
      const difference = next - previous;
      if (difference % 2 !== 0) {
        this.invertBanding = !this.invertBanding;
      }
      if (this.scrollLock) {
        this.prependCount = difference;
        if (this.scrollParent.scrollHeight >
          this.scrollParent.clientHeight +
            this.scrollParent.scrollTop +
            this.prependCount * this.rowHeight) {
          this.applyScrollLock();
        }
      }
    }
    await this.hasRenderedWithCurrentRowCount();
    const trailingEdge = this.trailingEdge;
    this.calculateWindowing();
    if (trailingEdge === this.trailingEdge) {
      this.trailingEdgeChange(this.trailingEdge);
    }
  }
  trailingEdgeChange(trailingEdge) {
    const to = Math.min(trailingEdge + (this.windowSize - 1), this.rowCount - 1);
    this.loadBlock.emit({
      from: trailingEdge,
      to,
      count: to - trailingEdge + 1,
    });
    if (trailingEdge <= 0) {
      this.firstBlock.emit();
    }
    if (to >= this.rowCount - 1) {
      this.lastBlock.emit();
    }
  }
  /** Jump to the passed row, with smooth scroll and highlight, if specified. */
  async jumpToRow(index, { highlight = true, smooth = 'auto' } = {}) {
    if (index < 0 || index > this.rowCount) {
      throw new Error(`Index is out of bounds: ${index}`);
    }
    await this.hasRenderedWithCurrentRowCount();
    this.internalJumpToRow(index, { highlight, smooth });
  }
  render() {
    const fullHeight = this.fullHeight();
    const clippedHeight = Math.min(MAX_TABLE_HEIGHT, fullHeight);
    this.lastRenderRowCount = this.rowCount;
    return (h(Host, { "high-contrast": theme.isHighContrast(), dark: theme.isDark(), style: {
        height: `${clippedHeight}px`,
        minHeight: `${clippedHeight}px`,
      } }, h("div", { role: 'table', style: {
        gridTemplateColumns: this.gridTemplateColumns(),
        gridTemplateRows: this.gridTemplateRows(),
        top: `${this.reflowRow * this.rowHeight - this.warp}px`,
      } }, h(TableHeader, { columnGroups: this.getColumnGroups(), clickSort: this.clickSort, headless: this.headless, sort: this.sort, sticky: this.stickyHeader, headerHeight: this.headerHeight }), h("div", { class: 'before', style: { height: `${this.beforeHeight}px` } }, this.renderBefore?.(h)), Array.from({
      length: Math.min(this.windowSize, this.rowCount),
    }, (_, i) => {
      const index = i + this.trailingEdge;
      if (index > this.rowCount - 1)
        return;
      return this.renderRowGroup(this.getKeyFromIndex(index), index);
    }), h("div", { class: 'after', style: { height: `${this.afterHeight}px` } }, this.renderAfter?.(h)))));
  }
  static get is() { return "es-table-virtualized"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-table-virtualized.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-table-virtualized.css"]
    };
  }
  static get properties() {
    return {
      "identifier": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Passed to cell renderer as `parent`."
        },
        "attribute": "identifier",
        "reflect": false,
        "defaultValue": "'table-virtualized'"
      },
      "headless": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Do not render header."
        },
        "attribute": "headless",
        "reflect": false,
        "defaultValue": "false"
      },
      "stickyHeader": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Header sticks to scroll parent."
        },
        "attribute": "sticky-header",
        "reflect": false,
        "defaultValue": "true"
      },
      "getCellData": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, index: number) => any",
          "resolved": "(key: string, index: number) => any",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting the data from the row. By default, it assumes you passed an array of data as your columns."
        }
      },
      "getKeyFromIndex": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(index: number) => string",
          "resolved": "(index: number) => string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Sync function for converting an index into a key"
        },
        "defaultValue": "(i) => `${i}`"
      },
      "cells": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TableCells<any, any>",
          "resolved": "{ [x: string]: TableCell<any, any>; }",
          "references": {
            "TableCells": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::TableCells"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A record of table cell definitions."
        }
      },
      "columns": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "string[]",
          "resolved": "string[] | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "The order and keys of the cells to be rendered. If omitted, all cells will be rendered."
        }
      },
      "rowCount": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The total number of rows"
        },
        "attribute": "row-count",
        "reflect": false
      },
      "renderBefore": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "RenderFunction",
          "resolved": "((h: typeof h) => string | VNode | VNode[] | null) | undefined",
          "references": {
            "RenderFunction": {
              "location": "import",
              "path": "../../../types",
              "id": "src/types.ts::RenderFunction"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Display in a row before the first row"
        }
      },
      "renderAfter": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "RenderFunction",
          "resolved": "((h: typeof h) => string | VNode | VNode[] | null) | undefined",
          "references": {
            "RenderFunction": {
              "location": "import",
              "path": "../../../types",
              "id": "src/types.ts::RenderFunction"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Display in a row after the last row"
        }
      },
      "windowSize": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The size of the window to render"
        },
        "attribute": "window-size",
        "reflect": false,
        "defaultValue": "100"
      },
      "blockSize": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Groups rows into blocks"
        },
        "attribute": "block-size",
        "reflect": false,
        "defaultValue": "10"
      },
      "reflowSize": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The size of the grid rows before starting a reflow"
        },
        "attribute": "reflow-size",
        "reflect": false,
        "defaultValue": "1000"
      },
      "linkRowTo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(row: any) => string",
          "resolved": "((row: any) => string) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "A function to calculate a href from the cell data."
        }
      },
      "rowHeight": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The height (in pixels) of the row"
        },
        "attribute": "row-height",
        "reflect": false,
        "defaultValue": "50"
      },
      "headerHeight": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The height (in pixels) of the header"
        },
        "attribute": "header-height",
        "reflect": false,
        "defaultValue": "52"
      },
      "beforeHeight": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The height (in pixels) of the before"
        },
        "attribute": "before-height",
        "reflect": false,
        "defaultValue": "0"
      },
      "afterHeight": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The height (in pixels) of the after"
        },
        "attribute": "after-height",
        "reflect": false,
        "defaultValue": "0"
      },
      "rowTakesFocus": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If rows should be allowed to take focus"
        },
        "attribute": "row-takes-focus",
        "reflect": false
      },
      "rowClass": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(\n        row: any,\n        key: string,\n        index: number,\n    ) => Record<string, boolean> | string | undefined",
          "resolved": "(row: any, key: string, index: number) => string | Record<string, boolean> | undefined",
          "references": {
            "Record": {
              "location": "global",
              "id": "global::Record"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A function to calculate the class or classes of the row from the cellData."
        },
        "defaultValue": "() => undefined"
      },
      "sort": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TableSort",
          "resolved": "[key: string, order: SortOrder] | undefined",
          "references": {
            "TableSort": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::TableSort"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "How the table is sorted"
        }
      },
      "scrollLock": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If the table should lock scroll on appending events"
        },
        "attribute": "scroll-lock",
        "reflect": false
      },
      "extraCellProps": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, data: any) => Record<string, any>",
          "resolved": "((key: string, data: any) => Record<string, any>) | undefined",
          "references": {
            "Record": {
              "location": "global",
              "id": "global::Record"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Pass extra props to cells"
        }
      }
    };
  }
  static get states() {
    return {
      "trailingEdge": {},
      "reflowRow": {},
      "warp": {},
      "jumpHighlight": {},
      "invertBanding": {}
    };
  }
  static get events() {
    return [{
        "method": "clickRow",
        "name": "clickRow",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered whenever a row is clicked. The `detail` is the item in the row array."
        },
        "complexType": {
          "original": "ClickRow",
          "resolved": "ClickRow<any>",
          "references": {
            "ClickRow": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::ClickRow"
            }
          }
        }
      }, {
        "method": "clickSort",
        "name": "clickSort",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered whenever a sortable header is clicked"
        },
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        }
      }, {
        "method": "loadBlock",
        "name": "loadBlock",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered when a block is rendered"
        },
        "complexType": {
          "original": "LoadWindow",
          "resolved": "LoadWindow",
          "references": {
            "LoadWindow": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::LoadWindow"
            }
          }
        }
      }, {
        "method": "lastBlock",
        "name": "lastBlock",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered when the last block is rendered"
        },
        "complexType": {
          "original": "void",
          "resolved": "void",
          "references": {}
        }
      }, {
        "method": "firstBlock",
        "name": "firstBlock",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered when the first block is rendered"
        },
        "complexType": {
          "original": "void",
          "resolved": "void",
          "references": {}
        }
      }];
  }
  static get methods() {
    return {
      "jumpToRow": {
        "complexType": {
          "signature": "(index: number, { highlight, smooth }?: Partial<JumpOptions>) => Promise<void>",
          "parameters": [{
              "tags": [],
              "text": ""
            }, {
              "tags": [],
              "text": ""
            }],
          "references": {
            "Promise": {
              "location": "global",
              "id": "global::Promise"
            },
            "Partial": {
              "location": "global",
              "id": "global::Partial"
            },
            "JumpOptions": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::JumpOptions"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "Jump to the passed row, with smooth scroll and highlight, if specified.",
          "tags": []
        }
      }
    };
  }
  static get elementRef() { return "host"; }
  static get watchers() {
    return [{
        "propName": "jumpHighlight",
        "methodName": "jumpHighlightChange"
      }, {
        "propName": "sort",
        "methodName": "sortChange"
      }, {
        "propName": "rowCount",
        "methodName": "rowCountChange"
      }, {
        "propName": "trailingEdge",
        "methodName": "trailingEdgeChange"
      }];
  }
}
//# sourceMappingURL=es-table-virtualized.js.map
